import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl, apiConfig } from '../config/api';

interface AnalyticsSummary {
  totalMembers: number;
  leaders: number;
  visitors: number;
  attendees: number;
  members: number;
  engagedMembers: number;
  recentActivity: number;
}

interface MemberJourneyData {
  id: string;
  firstName: string;
  lastName: string;
  status: string;
  memberSince: string;
  checkInsCount: number;
  groupsCount: number;
  servicesCount: number;
  totalGiving: number;
  registrationsCount: number;
  journeyStage: string;
  lastActivity: string;
}

interface DashboardData {
  summary: AnalyticsSummary;
  memberJourney: MemberJourneyData[];
}

const Dashboard: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch summary data
      const summaryResponse = await fetch(buildApiUrl(apiConfig.endpoints.analytics.summary));
      const summaryData = await summaryResponse.json();

      if (!summaryData.success) {
        throw new Error(summaryData.error?.message || 'Failed to fetch summary data');
      }

      // Fetch member journey data
      const journeyResponse = await fetch(buildApiUrl(apiConfig.endpoints.analytics.memberJourney) + '?limit=20');
      const journeyData = await journeyResponse.json();

      if (!journeyData.success) {
        throw new Error(journeyData.error?.message || 'Failed to fetch member journey data');
      }

      setDashboardData({
        summary: summaryData.data.summary,
        memberJourney: journeyData.data
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log('Dashboard useEffect triggered - isAuthenticated:', isAuthenticated, 'dashboardData:', !!dashboardData, 'loading:', loading);
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Fetch data if authenticated and we don't have data yet
    if (isAuthenticated && !dashboardData) {
      console.log('Fetching dashboard data...');
      fetchDashboardData();
    }
  }, [isAuthenticated, navigate, fetchDashboardData, dashboardData]); // Remove loading from dependencies to avoid loops

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getJourneyStageColor = (stage: string) => {
    switch (stage) {
      case 'Engaged Member':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Member':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Attendee':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Visitor':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getJourneyStageIcon = (stage: string) => {
    switch (stage) {
      case 'Engaged Member':
        return '⭐';
      case 'Member':
        return '👥';
      case 'Attendee':
        return '📊';
      case 'Visitor':
        return '👋';
      default:
        return '👤';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing dashboard...</p>
        </div>
      </div>
    );
  }

  const { summary, memberJourney } = dashboardData;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Grace Fellowship Analytics Dashboard</h1>
              <p className="text-gray-600 mt-1">Welcome back, {user?.firstName || 'User'}!</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Last updated</p>
              <p className="text-sm font-medium text-gray-900">{new Date().toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-blue-600 text-lg">👥</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Members</p>
                <p className="text-2xl font-bold text-gray-900">{summary.totalMembers.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                  <span className="text-red-600 text-lg">👑</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Leaders</p>
                <p className="text-2xl font-bold text-gray-900">{summary.leaders}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <span className="text-emerald-600 text-lg">⭐</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Engaged Members</p>
                <p className="text-2xl font-bold text-gray-900">{summary.engagedMembers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-blue-600 text-lg">👥</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Members</p>
                <p className="text-2xl font-bold text-gray-900">{summary.members}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <span className="text-yellow-600 text-lg">📊</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Attendees</p>
                <p className="text-2xl font-bold text-gray-900">{summary.attendees}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <span className="text-gray-600 text-lg">👋</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Visitors</p>
                <p className="text-2xl font-bold text-gray-900">{summary.visitors}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <span className="text-purple-600 text-lg">📈</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Recent Activity</p>
                <p className="text-2xl font-bold text-gray-900">{summary.recentActivity}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Journey Stage Chart */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Member Journey Distribution</h2>
            <p className="text-sm text-gray-600 mt-1">Breakdown of members by engagement level</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {[
                { stage: 'Leader', count: summary.leaders, color: 'bg-red-500' },
                { stage: 'Engaged Member', count: summary.engagedMembers, color: 'bg-emerald-500' },
                { stage: 'Member', count: summary.members, color: 'bg-blue-500' },
                { stage: 'Attendee', count: summary.attendees, color: 'bg-yellow-500' },
                { stage: 'Visitor', count: summary.visitors, color: 'bg-gray-500' }
              ].map((item) => {
                const percentage = summary.totalMembers > 0 ? (item.count / summary.totalMembers) * 100 : 0;
                return (
                  <div key={item.stage} className="text-center">
                    <div className="relative">
                      <svg className="w-24 h-24 mx-auto" viewBox="0 0 36 36">
                        <path
                          d="M18 2.0845
                            a 15.9155 15.9155 0 0 1 0 31.831
                            a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="3"
                        />
                        <path
                          d="M18 2.0845
                            a 15.9155 15.9155 0 0 1 0 31.831
                            a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke={item.color.replace('bg-', '#')}
                          strokeWidth="3"
                          strokeDasharray={`${percentage}, 100`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-sm font-semibold text-gray-900">{Math.round(percentage)}%</span>
                      </div>
                    </div>
                    <p className="text-sm font-medium text-gray-900 mt-2">{item.stage}</p>
                    <p className="text-xs text-gray-500">{item.count} members</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Member Journey Table */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Member Journey Details</h2>
            <p className="text-sm text-gray-600 mt-1">Recent member activity and engagement levels</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Journey Stage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Check-ins
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Groups
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Member Since
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Activity
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {memberJourney.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700">
                              {member.firstName.charAt(0)}{member.lastName.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {member.firstName} {member.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{member.status}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getJourneyStageColor(member.journeyStage)}`}>
                        <span className="mr-1">{getJourneyStageIcon(member.journeyStage)}</span>
                        {member.journeyStage}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {member.checkInsCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {member.groupsCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(member.memberSince)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(member.lastActivity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 